import { FolderOutlined, FolderOpenOutlined, DatabaseOutlined, BookOutlined, LockOutlined } from '@ant-design/icons';
import { Link, useParams, useNavigate } from '@tanstack/react-router';
import ClickToEditMulti from '@zeus/core/ClickToEditMulti';
import HomeHistoryHeader from '@zeus/core/HomeHistoryHeader';
import PermitOpsEngineer from '@zeus/core/PermitOpsEngineer';
import PermitSourceEngineer from '@zeus/core/PermitSourceEngineer';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useOrgCollections } from '_/hooks/useCollections';
import { useDestinations } from '_/hooks/useDestinations';
import useOrgAccess from '_/hooks/useOrgAccess';
import { useOrganization as useOrganizationHook, useOrgReadme, useUpdateOrgReadme } from '_/hooks/useOrganizations';
import { useProjects } from '_/hooks/useProjects';
import { useSchemas } from '_/hooks/useSchemas';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Table, Button, Space, Card } from 'antd';
import type { ColumnType } from 'antd/lib/table';
import React, { useCallback, useMemo, ReactElement, useEffect } from 'react';

interface Project {
  id: string;
  name: string;
  slug: string;
  locked?: boolean;
  environment?: string;
}

interface Collection {
  id: string;
  name: string;
  slug: string;
  projectId?: string;
  projectSlug?: string;
}

interface Destination {
  id: string;
  name: string;
  type?: string;
}

interface Schema {
  id: string;
  name: string;
  slug: string;
}

function OrgHome(): ReactElement {
  const params = useParams({ strict: false }) as Partial<{ orgSlug: string }>;
  const orgSlug = params.orgSlug || '';
  const history = useNavigate();
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);

  // Fetch data using React Query
  const { data: organization } = useOrganizationHook(orgSlug);
  const { selectedOrganization, selectedOrgSlug } = useOrganization();
  const { data: orgReadme = '', isLoading: isFetchingOrgReadme } = useOrgReadme(organization?.id);
  const { data: projectsData = [], isLoading: isFetchingProjects } = useProjects(orgSlug);
  const { data: orgCollectionsData = [], isLoading: isFetchingOrgCollections } = useOrgCollections(orgSlug);
  const { data: destinations = [], isLoading: isFetchingDestinations } = useDestinations();
  const { data: schemas = [], isLoading: isFetchingSchemas } = useSchemas(orgSlug);
  const updateReadmeMutation = useUpdateOrgReadme();

  // Get layout context
  const { setBreadcrumbs } = useTopHeader();

  // Set up header with breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      {
        title: selectedOrganization?.name || organization?.name || '',
        href: `/orgs/${selectedOrgSlug || orgSlug}`,
      },
      { title: 'Home' },
    ]);

    return () => {
      setBreadcrumbs([]);
    };
  }, [setBreadcrumbs, selectedOrganization, organization, selectedOrgSlug, orgSlug]);

  // Combine collections with project slugs (mirroring getOrgCollectionsWithProjects selector)
  const collections = useMemo(() => {
    return orgCollectionsData.map((collection: Collection) => {
      const project = projectsData.find((p: Project) => p.id === collection.projectId);
      if (project) {
        return { ...collection, projectSlug: project.slug };
      }
      return collection;
    });
  }, [orgCollectionsData, projectsData]);

  const handleViewReadmeHistory = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (organization) {
        const path = `/orgs/${organization.slug}/readmeversions`;
        void history({ to: path, state: { organization: organization } });
      }
    },
    [history, organization],
  );

  const handleSaveReadmeText = useCallback(
    (readme: string) => {
      if (readme && organization?.id) {
        updateReadmeMutation.mutate({ orgId: organization.id, content: readme });
      }
    },
    [updateReadmeMutation, organization?.id],
  );

  // Table columns
  const projectColumns: ColumnType<Project>[] = [
    {
      title: '',
      key: 'icon',
      width: 50,
      render: (_, record: Project) => (record.locked ? <LockOutlined /> : <FolderOutlined />),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Project) => (
        <Link to={`/orgs/${organization?.slug}/projects/${record.slug}`} data-test-id="org-project-link">
          {name}
        </Link>
      ),
    },
    {
      title: 'Environment',
      dataIndex: 'environment',
      key: 'environment',
    },
  ];

  const collectionColumns: ColumnType<Collection>[] = [
    {
      title: '',
      key: 'icon',
      width: 50,
      render: () => <FolderOpenOutlined />,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Collection) => (
        <Link to={`/orgs/${organization?.slug}/projects/${record.projectSlug}/collections/${record.slug}`}>{name}</Link>
      ),
    },
  ];

  const destinationColumns: ColumnType<Destination>[] = [
    {
      title: '',
      key: 'icon',
      width: 50,
      render: () => <DatabaseOutlined />,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Destination) => (
        <Link to={`/orgs/${organization?.slug}/destinations/${record.name}`}>{name}</Link>
      ),
    },
  ];

  const schemaColumns: ColumnType<Schema>[] = [
    {
      title: '',
      key: 'icon',
      width: 50,
      render: () => <BookOutlined />,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Schema) => (
        <Link to={`/orgs/${organization?.slug}/schemas/${record.slug}`} data-test-id="org-schema-link">
          {name}
        </Link>
      ),
    },
  ];

  return (
    <>
      {/* Readme Section */}
      <Card
        loading={isFetchingOrgReadme}
        title={<HomeHistoryHeader handleViewHistory={handleViewReadmeHistory} resetStylesForCardHeader />}
        style={{ marginBottom: 24, maxWidth: 800, margin: '0 auto 24px' }}
      >
        <ClickToEditMulti disabled={!access} onSaveClick={handleSaveReadmeText} text={orgReadme} />
      </Card>

      {/* Projects Table */}
      <Card
        title="Projects"
        style={{ marginBottom: 24 }}
        extra={
          <Space>
            <PermitOpsEngineer>
              <Button
                type="primary"
                onClick={() => organization && void history({ to: `/orgs/${organization.slug}/projects/_new` })}
                data-test-id="org-proj-card-create-new-proj-btn"
              >
                Create New
              </Button>
            </PermitOpsEngineer>
            <Button onClick={() => organization && void history({ to: `/orgs/${organization.slug}/projects` })}>View All</Button>
          </Space>
        }
      >
        <Table
          columns={projectColumns}
          dataSource={projectsData}
          loading={isFetchingProjects}
          pagination={false}
          rowKey="id"
          size="small"
        />
      </Card>

      {/* Collections Table */}
      <Card title="Collections" style={{ marginBottom: 24 }}>
        <Table
          columns={collectionColumns}
          dataSource={collections}
          loading={isFetchingOrgCollections}
          pagination={false}
          rowKey="id"
          size="small"
        />
      </Card>

      {/* Destinations Table */}
      <Card
        title="Destinations"
        style={{ marginBottom: 24 }}
        extra={
          <Space>
            <PermitSourceEngineer>
              <Button
                type="primary"
                onClick={() => organization && void history({ to: `/orgs/${organization.slug}/destinations/_new` })}
              >
                Create New
              </Button>
            </PermitSourceEngineer>
            <Button onClick={() => organization && void history({ to: `/orgs/${organization.slug}/destinations` })}>
              View All
            </Button>
          </Space>
        }
      >
        <Table
          columns={destinationColumns}
          dataSource={destinations}
          loading={isFetchingDestinations}
          pagination={false}
          rowKey="id"
          size="small"
        />
      </Card>

      {/* Schemas Table */}
      <Card
        title="Schemas"
        style={{ marginBottom: 24 }}
        extra={
          <Space>
            <PermitSourceEngineer>
              <Button
                type="primary"
                onClick={() => organization && void history({ to: `/orgs/${organization.slug}/schemas/_new` })}
                data-test-id="org-schema-card-create-new-schema-btn"
              >
                Create New
              </Button>
            </PermitSourceEngineer>
            <Button onClick={() => organization && void history({ to: `/orgs/${organization.slug}/schemas` })}>View All</Button>
          </Space>
        }
      >
        <Table
          columns={schemaColumns}
          dataSource={schemas}
          loading={isFetchingSchemas}
          pagination={false}
          rowKey="id"
          size="small"
        />
      </Card>
    </>
  );
}

export default OrgHome;
